import React from 'react';
import Navigation from '../components/Navigation';
import CostcoFooter from '../components/CostcoFooter';
import SEO from '../components/SEO';
import { shopCardRows } from '../lib/shopCardScale';
import { FileText, AlertCircle, CheckCircle } from 'lucide-react';

const CostcoTerms = () => {
  const tableOfContents = [
    { id: 'promotion-overview', title: 'Promotion Terms and Conditions' },
    { id: 'shop-card-values', title: 'Shop Card Values' },
    { id: 'eligibility', title: 'Eligibility Requirements' },
    { id: 'exclusions', title: 'Exclusions and Limitations' },
    { id: 'privacy', title: 'Privacy' },
    { id: 'general-terms', title: 'General Terms' },
    { id: 'contact', title: 'Customer Support' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO title="Costco Promotion Terms" description="Terms and conditions for the Costco Executive Member Shop Card promotion." path="/costco-terms" />
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-12 bg-gradient-to-br from-[#00594C] to-[#003d35] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md px-6 py-3 rounded-full mb-6 border border-white/20">
              <FileText className="w-5 h-5 text-[#99DF63]" />
              <span className="font-semibold">Legal Information</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Costco Member Promotion
              <span className="block text-[#99DF63]">Terms & Conditions</span>
            </h1>
            <p className="text-lg text-gray-200">
              Last Updated: November 2024
            </p>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-8 bg-gray-50 border-b">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-[#00594C] mb-4">Contents</h2>
            <div className="grid md:grid-cols-2 gap-3">
              {tableOfContents.map((item, index) => (
                <a
                  key={index}
                  href={`#${item.id}`}
                  className="flex items-center space-x-2 text-[#00594C] hover:text-[#99DF63] transition-colors"
                >
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{item.title}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-12">

            {/* Important Notice */}
            <div className="bg-[#99DF63]/10 border-l-4 border-[#99DF63] rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-6 h-6 text-[#00594C] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-[#00594C] mb-2">Important Notice</h3>
                  <p className="text-gray-700">
                    This is a promotional offer. Standard loan terms and conditions apply. 
                    Please read all terms carefully before applying.
                  </p>
                </div>
              </div>
            </div>

            {/* Promotion Overview */}
            <div id="promotion-overview" className="scroll-mt-20">
              <h2 className="text-3xl font-bold text-[#00594C] mb-6">Promotion Terms and Conditions</h2>
              
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300">
                  <tbody>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 p-4 font-bold text-[#00594C] w-1/3">
                        Promotion Overview
                      </td>
                      <td className="border border-gray-300 p-4">
                        This promotion is offered by Costco Wholesale Australia (ABN 57 104 012 893) (<strong>Promoter</strong>) and 
                        Manage Your Loans Pty Ltd (ABN 34 652 442 790, Credit Representative 540988).
                        <br/><br/>
                        The offer consists of providing eligible Costco Executive Members who successfully settle a loan and meet 
                        the criteria below, a digital Costco Shop Card via email. Shop Card values vary based on loan type and amount.
                      </td>
                    </tr>
                    
                    <tr>
                      <td className="border border-gray-300 p-4 font-bold text-[#00594C]">
                        Promotional Period
                      </td>
                      <td className="border border-gray-300 p-4">
                        <strong>Start:</strong> 12:00 AM, Monday, 1st December 2024 (AEST)<br/>
                        <strong>End:</strong> 11:59 PM, Wednesday, 31st December 2027 (AEST)<br/>
                        <em className="text-sm text-gray-600">The Promotional Period may be extended at the Promoter's discretion.</em>
                      </td>
                    </tr>

                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 p-4 font-bold text-[#00594C]">
                        Partnership Details
                      </td>
                      <td className="border border-gray-300 p-4">
                        Manage Your Loans Pty Ltd (MYL) ACN 652 442 790 Credit Representative (CR) 540988 provides access to 
                        Credit services via Home Mortgage Plus Pty Ltd ACN 105 991 839, Australian Credit License (ACL) 392200.
                        <br/><br/>
                        A referral arrangement is in place between MYL and Costco; however, they are not otherwise affiliated 
                        in any other way.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Shop Card Values */}
            <div id="shop-card-values" className="scroll-mt-20">
              <h2 className="text-3xl font-bold text-[#00594C] mb-6">Shop Card Values</h2>
              
              <p className="mb-6 text-gray-700">
                Costco Shop Card values operate on a tiered system based on your settled loan amount. 
                <strong> Loan Amount</strong> means the portion of the approved loan that is fully drawn down, 
                excluding any funds sitting in offset or redraw accounts, as well as any undrawn amounts.
              </p>

              <div className="space-y-8">
                {/* Home & Business Loans */}
                <div>
                  <h3 className="text-xl font-bold text-[#00594C] mb-4">Home & Business Loans</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300">
                      <thead className="bg-[#00594C] text-white">
                        <tr>
                          <th className="border border-gray-300 p-3 text-left">Loan Amount</th>
                          <th className="border border-gray-300 p-3 text-left">Shop Card Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shopCardRows('home').map((row, i) => (
                          <tr key={i} className={i % 2 === 0 ? 'bg-gray-50' : ''}>
                            <td className="border border-gray-300 p-3">{row[0]}</td>
                            <td className="border border-gray-300 p-3 font-semibold">{row[1]}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Car & Asset Finance */}
                <div>
                  <h3 className="text-xl font-bold text-[#00594C] mb-4">Car & Asset Finance</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300">
                      <thead className="bg-[#00594C] text-white">
                        <tr>
                          <th className="border border-gray-300 p-3 text-left">Loan Amount</th>
                          <th className="border border-gray-300 p-3 text-left">Shop Card Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shopCardRows('car').map((row, i) => (
                          <tr key={i} className={i % 2 === 0 ? 'bg-gray-50' : ''}>
                            <td className="border border-gray-300 p-3">{row[0]}</td>
                            <td className="border border-gray-300 p-3 font-semibold">{row[1]}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Personal Loans */}
                <div>
                  <h3 className="text-xl font-bold text-[#00594C] mb-4">Personal Loans</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300">
                      <thead className="bg-[#00594C] text-white">
                        <tr>
                          <th className="border border-gray-300 p-3 text-left">Loan Amount</th>
                          <th className="border border-gray-300 p-3 text-left">Shop Card Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shopCardRows('personal').map((row, i) => (
                          <tr key={i} className={i % 2 === 0 ? 'bg-gray-50' : ''}>
                            <td className="border border-gray-300 p-3">{row[0]}</td>
                            <td className="border border-gray-300 p-3 font-semibold">{row[1]}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Eligibility */}
            <div id="eligibility" className="scroll-mt-20">
              <h2 className="text-3xl font-bold text-[#00594C] mb-6">Eligibility Requirements</h2>
              
              <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-[#99DF63] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-[#00594C]">Active Costco Executive Membership</p>
                    <p className="text-gray-700">You must be an active Australian Costco Executive Member at the time of loan application AND when the Shop Card is issued.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-[#99DF63] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-[#00594C]">Valid Membership Number</p>
                    <p className="text-gray-700">A valid Costco Executive Membership number must be provided at the time of loan application.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-[#99DF63] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-[#00594C]">Loan Settlement</p>
                    <p className="text-gray-700">The loan must be approved, settled, and fully drawn down. Balances in offset or redraw accounts are excluded from Shop Card calculations.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-[#99DF63] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-[#00594C]">90-Day Maintenance Period</p>
                    <p className="text-gray-700">Shop Cards are issued via email by the Costco Services Team at least 90 days after loan settlement, provided both the loan and Executive Membership remain active.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-[#99DF63] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-[#00594C]">Australian Residency</p>
                    <p className="text-gray-700">Applicant must be an Australian resident aged 18 years or over with an Australian address.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-[#99DF63] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-[#00594C]">Exclusive Offer</p>
                    <p className="text-gray-700">This offer cannot be used in conjunction with any other promotional offer, discount, or special pricing from Manage Your Loans.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Exclusions */}
            <div id="exclusions" className="scroll-mt-20">
              <h2 className="text-3xl font-bold text-[#00594C] mb-6">Exclusions and Limitations</h2>
              
              <div className="space-y-4 text-gray-700">
                <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4">
                  <p className="font-semibold text-red-900 mb-2">You will NOT receive a Shop Card if:</p>
                  <ul className="list-disc list-inside space-y-2 ml-2">
                    <li>The loan is not approved or not settled</li>
                    <li>The loan is not fully drawn down (including balances in offset or redraw)</li>
                    <li>A new loan is refinanced before the second anniversary of the original loan settled by Manage Your Loans, for which you have already received a Shop Card</li>
                    <li>The policy or membership is cancelled within the 90-day period after settlement</li>
                    <li>Your Costco Executive Membership is not active at the scheduled Shop Card issuance date</li>
                    <li>You have used any other promotional offer or discount that is not compatible with this Costco offer</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-[#00594C] mb-3">Shop Card Conditions</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start space-x-2">
                      <span className="text-[#99DF63] mt-1">•</span>
                      <span>The Costco Shop Card has no expiration date and cannot be exchanged for cash.</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#99DF63] mt-1">•</span>
                      <span>The Shop Card is not transferable or exchangeable and may only be received by the eligible member.</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#99DF63] mt-1">•</span>
                      <span>Cards are subject to individual merchant acceptance and Costco's Shop Card Terms and Conditions.</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#99DF63] mt-1">•</span>
                      <span>Any unused balance of the card will not be awarded as cash.</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#99DF63] mt-1">•</span>
                      <span>Limited to one Shop Card per Executive Membership number per loan settlement.</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-[#00594C] mb-3">Loan Amount Calculation Example</h3>
                  <div className="bg-[#99DF63]/10 rounded-lg p-4">
                    <p className="mb-2"><strong>Example:</strong></p>
                    <p>If you have a $600,000 loan settled, with $60,000 in redraw or offset:</p>
                    <p className="font-semibold mt-2">Loan Amount = Total Settled - (Redraw + Offset)</p>
                    <p className="font-semibold">$600,000 - $60,000 = $540,000</p>
                    <p className="mt-2 text-sm">Your Shop Card value would be calculated based on $540,000, not $600,000.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Privacy */}
            <div id="privacy" className="scroll-mt-20">
              <h2 className="text-3xl font-bold text-[#00594C] mb-6">Privacy</h2>
              
              <div className="space-y-4 text-gray-700">
                <p>
                  The Promoter (or third parties on its behalf) may collect personal information to conduct the Promotion 
                  and disclose such information to third parties for this purpose, including agents, contractors, service 
                  providers, and Costco Wholesale Australia.
                </p>

                <p>
                  In order to facilitate the Promotion, Manage Your Loans will disclose the eligible member's name, 
                  Costco Executive Membership number, contact details, and loan settlement information to Costco, 
                  so that Costco can send the Shop Card.
                </p>

                <p>
                  All members consent to their personal information being collected, used, and stored for this purpose 
                  in accordance with Manage Your Loans' Privacy Policy, which forms part of these Terms and Conditions.
                </p>

                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="font-semibold mb-2">For more information:</p>
                  <ul className="space-y-1">
                    <li>• Manage Your Loans Privacy Policy: <a href="/privacy-policy" className="text-[#00594C] underline">manageyourloans.com.au/privacy-policy</a></li>
                    <li>• Costco Privacy Policy: <a href="https://www.costco.com.au/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#00594C] underline">costco.com.au/privacy-policy</a></li>
                  </ul>
                </div>
              </div>
            </div>

            {/* General Terms */}
            <div id="general-terms" className="scroll-mt-20">
              <h2 className="text-3xl font-bold text-[#00594C] mb-6">General Terms</h2>
              
              <div className="space-y-4 text-gray-700">
                <div>
                  <h3 className="font-bold text-[#00594C] mb-2">1. Shop Card Delivery</h3>
                  <p>Eligible members will receive their digital Shop Card via their nominated email at least 90 days after loan settlement, provided all eligibility criteria are met and the loan and Executive Membership remain active.</p>
                </div>

                <div>
                  <h3 className="font-bold text-[#00594C] mb-2">2. Verification</h3>
                  <p>If additional information is needed to confirm eligibility, the Promoter will notify the member by email. The member will have 14 days from the email date to respond, unless otherwise specified. Failure to provide the requested information may result in Shop Card invalidation.</p>
                </div>

                <div>
                  <h3 className="font-bold text-[#00594C] mb-2">3. Member Responsibility</h3>
                  <p>Eligible members are responsible for ensuring correct contact details, Costco membership number, email address, and postal address are provided. The Promoter accepts no responsibility for Shop Card delivery failures due to incorrect or outdated information.</p>
                </div>

                <div>
                  <h3 className="font-bold text-[#00594C] mb-2">4. Modification Rights</h3>
                  <p>The Promoter reserves the right to verify eligibility, modify terms, suspend, or terminate the Promotion at any time due to circumstances beyond reasonable control, including but not limited to changes in lending regulations, lender relationships, or Costco partnership terms.</p>
                </div>

                <div>
                  <h3 className="font-bold text-[#00594C] mb-2">5. Liability Limitation</h3>
                  <p>Subject to applicable consumer protection laws, the Promoter excludes all liability for any loss, damage, or personal injury arising from the Promotion, including technical difficulties, theft, unauthorized access, or variation in Shop Card value.</p>
                </div>

                <div>
                  <h3 className="font-bold text-[#00594C] mb-2">6. Consumer Rights</h3>
                  <p>Nothing in these Terms and Conditions is intended to exclude, restrict, or modify consumer rights under the Competition and Consumer Act 2010 (Cth) or other applicable legislation that cannot be excluded by agreement.</p>
                </div>

                <div>
                  <h3 className="font-bold text-[#00594C] mb-2">7. Governing Law</h3>
                  <p>These Terms and Conditions are governed by the laws of New South Wales, Australia. Participants submit to the non-exclusive jurisdiction of the courts of New South Wales.</p>
                </div>

                <div>
                  <h3 className="font-bold text-[#00594C] mb-2">8. Costco Shop Card Terms</h3>
                  <p>For full Costco Shop Card Terms and Conditions, visit: <a href="https://www.costco.com.au/costco-shop-card-TC" target="_blank" rel="noopener noreferrer" className="text-[#00594C] underline">costco.com.au/costco-shop-card-TC</a></p>
                </div>
              </div>
            </div>

            {/* Customer Support */}
            <div id="contact" className="scroll-mt-20">
              <h2 className="text-3xl font-bold text-[#00594C] mb-6">Customer Support</h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-[#00594C] to-[#003d35] text-white rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4">Manage Your Loans Support</h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-300">Phone</p>
                      <a href="tel:+61468007056" className="text-lg font-semibold hover:text-[#99DF63]">0468 007 056</a>
                    </div>
                    <div>
                      <p className="text-sm text-gray-300">Email</p>
                      <a href="mailto:admin@manageyourloans.com.au" className="text-lg font-semibold hover:text-[#99DF63]">admin@manageyourloans.com.au</a>
                    </div>
                    <div>
                      <p className="text-sm text-gray-300">Hours</p>
                      <p className="text-lg font-semibold">8am to 8pm Monday to Friday AEST</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-6 border-2 border-gray-200">
                  <h3 className="text-xl font-bold text-[#00594C] mb-4">Costco Shop Card Support</h3>
                  <div className="space-y-3">
                    <p className="text-gray-700">For Costco Shop Card inquiries, please contact Costco directly:</p>
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <a href="mailto:costcoservices@costco.com.au" className="text-lg font-semibold text-[#00594C] hover:text-[#99DF63]">costcoservices@costco.com.au</a>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Shop Card Terms</p>
                      <a href="https://www.costco.com.au/costco-shop-card-TC" target="_blank" rel="noopener noreferrer" className="text-[#00594C] underline hover:text-[#99DF63]">View Costco Shop Card T&Cs</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-6 mt-12">
              <h3 className="font-bold text-yellow-900 mb-3">Important Disclaimer</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                <strong>Manage Your Loans Pty Ltd</strong> ABN 34 652 442 790 is a Credit Representative (540988) 
                of <strong>Home Mortgage Plus Pty Ltd</strong> ACN 105 991 839 (ACL 392200). This promotional offer 
                is subject to lending criteria, approval, and settlement. The Costco Shop Card promotion is separate 
                from and does not affect your loan terms, interest rates, fees, or conditions. All loan applications 
                are subject to responsible lending obligations and lender approval. This promotion may be varied, 
                withdrawn, or terminated at any time.
              </p>
            </div>

          </div>
        </div>
      </section>

      <CostcoFooter />
    </div>
  );
};

export default CostcoTerms;
